import { useState, useEffect, useRef } from 'react';

import { fetchNextEvent, shortEventTitle, formatShortDate } from '../utils/upcomingEvent';

// import ContactModal from './ContactModal'; // TODO: re-enable if contact form returns

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const DIRECT_LINE = 'tel:+13524280086';
const DISMISS_KEY = 'dismissed_announcement';
const DISMISS_DAYS = 30;
export const ROTATE_MS = 6000;

// Ids dismissed within the last 30 days. Older entries stored a single `id`.
const getDismissedIds = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(DISMISS_KEY));
    if (!stored) return [];
    const daysSince = (Date.now() - stored.at) / (1000 * 60 * 60 * 24);
    if (daysSince >= DISMISS_DAYS) return [];
    return stored.ids || [stored.id];
  } catch {
    return []; // Corrupted data, show the announcements
  }
};

// cta_action holds a site path or URL; anything else falls back to the direct line.
const announcementHref = (ctaAction) =>
  ctaAction && /^(\/|https?:\/\/)/.test(ctaAction) ? ctaAction : DIRECT_LINE;

const eventItem = (event) => {
  const title = shortEventTitle(event.title);
  return {
    id: `event-${event.id}`,
    message: `${title}: ${formatShortDate(event.date)}`,
    highlights: [title],
    ctaText: 'Details →',
    href: '/events',
  };
};

const announcementItem = (announcement) => ({
  id: announcement.id,
  message: announcement.message,
  highlights: ['Yoga', 'Tai Chi'],
  ctaText: announcement.cta_text,
  href: announcementHref(announcement.cta_action),
});

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const renderMessage = ({ message, highlights }) => {
  const pattern = new RegExp(`(${highlights.map(escapeRegExp).join('|')})`, 'g');
  return message.split(pattern).map((part, i) =>
    highlights.includes(part) ? (
      <span key={i} style={{ color: '#fde047', fontWeight: 700, letterSpacing: '0.02em' }}>{part}</span>
    ) : part
  );
};

/**
 * Dismissible top ribbon. Rotates between the next upcoming event (which drops
 * out on its own once the event has passed) and the active admin announcement.
 */
const AnnouncementRibbon = () => {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [ribbonHeight, setRibbonHeight] = useState(0);
  const ribbonRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchAnnouncement = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/announcements/active`);
        return res.ok ? await res.json() : null;
      } catch {
        return null; // Silently fail — ribbon is non-critical
      }
    };
    Promise.all([fetchNextEvent(), fetchAnnouncement()]).then(([event, announcement]) => {
      if (cancelled) return;
      const dismissedIds = getDismissedIds();
      setItems(
        [event && eventItem(event), announcement && announcementItem(announcement)]
          .filter((item) => item && !dismissedIds.includes(item.id))
      );
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (items.length < 2 || paused) return undefined;
    const timer = setInterval(() => setActive((i) => (i + 1) % items.length), ROTATE_MS);
    return () => clearInterval(timer);
  }, [items.length, paused]);

  useEffect(() => {
    if (ribbonRef.current) {
      setRibbonHeight(ribbonRef.current.offsetHeight);
    }
  }, [items, dismissed]);

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, JSON.stringify({ ids: items.map((item) => item.id), at: Date.now() }));
    setDismissed(true);
  };

  if (!items.length || dismissed) return null;

  return (
    <>
      <div
        ref={ribbonRef}
        role="region"
        aria-label="Announcements"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 60,
          background: 'linear-gradient(to right, #b91c1c, #7f1d1d)',
          color: 'white',
          padding: '10px 16px',
        }}
      >
        {/* All items share one grid cell, so the ribbon sizes to the tallest and never jumps. */}
        <div style={{ display: 'grid', maxWidth: '1200px', margin: '0 auto', paddingRight: '32px' }}>
          {items.map((item, i) => {
            const isActive = i === active;
            return (
              <div
                key={item.id}
                aria-hidden={!isActive}
                data-testid="ribbon-item"
                className="motion-reduce:transition-none"
                style={{
                  gridArea: '1 / 1',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  fontSize: '15px',
                  opacity: isActive ? 1 : 0,
                  visibility: isActive ? 'visible' : 'hidden',
                  transition: 'opacity 500ms ease, visibility 500ms',
                }}
              >
                <span style={{ textAlign: 'center', lineHeight: 1.4 }}>
                  <span style={{ color: '#fde047', marginRight: '6px' }}>✦</span>
                  {renderMessage(item)}
                </span>
                <a
                  href={item.href}
                  tabIndex={isActive ? 0 : -1}
                  style={{
                    flexShrink: 0,
                    background: 'rgba(255,255,255,0.9)',
                    color: '#991b1b',
                    fontWeight: 600,
                    fontSize: '13px',
                    padding: '5px 16px',
                    borderRadius: '9999px',
                    textDecoration: 'none',
                  }}
                >
                  {item.ctaText}
                </a>
              </div>
            );
          })}
        </div>
        <button
          onClick={handleDismiss}
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'rgba(255,255,255,0.7)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            fontSize: '18px',
            lineHeight: 1,
          }}
          aria-label="Dismiss announcement"
        >
          ✕
        </button>
      </div>
      {/* Spacer to push content below the fixed ribbon */}
      <div style={{ height: ribbonHeight }} />
      {/* ContactModal removed — all CTAs now call Jim Rivera's direct line tel:+13524280086 */}
      {/* TODO: re-enable <ContactModal> here if contact form returns */}
    </>
  );
};

export default AnnouncementRibbon;
